"use client";

import type { GameData } from "@/app/games/(details)/types";
import { trackLegacyEvent } from "@analytics";
import {
  Box,
  CloudImage,
  Divider,
  Heading,
  Stack,
  useIntersectionObserver,
  useTheme,
} from "@biom3/react";
import { PlayButton } from "@ui-kit/CTAButtons/PlayButton";
import { useIsLessThanMediumScreen } from "@ui-kit/hooks/screenSizeHooks";
import { useMemo } from "react";

type TwitchDropsGameHeaderProps = {
  stickyPageTitle?: string;
  gameData: GameData;
  playLink: string;
};

const MOTION_SX = {
  transitionDuration: "base.motion.normal.gentle.cssDuration",
  transitionTimingFunction: "base.motion.normal.gentle.cssTimingFunction",
};

export const TwitchDropsGameHeader = ({
  gameData,
  playLink,
  stickyPageTitle,
}: TwitchDropsGameHeaderProps) => {
  const { base } = useTheme();
  const { entry, domRef } = useIntersectionObserver({
    threshold: 0,
  });
  const isLessThanMedium = useIsLessThanMediumScreen();
  const isSticky = useMemo(
    () => !!entry && entry.isIntersecting === false,
    [entry],
  );

  const onPlayButtonClick = (link: string) => {
    trackLegacyEvent({
      screen: "TwitchDrops",
      userJourney: "Games",
      control: "PlayNowClicked",
      controlType: "Button",
      extras: {
        gameName: gameData.details.name ?? "",
        gameId: gameData.details.game_id,
        website: link,
        identifier: "TwitchDropsGameHeader",
      },
    });
  };

  return (
    <>
      <div ref={domRef} style={{ height: 1, width: "100%" }} />
      <Box
        sx={{
          pos: { default: "static", medium: "sticky" },
          px: {
            default: "unset",
            medium: isSticky ? "base.spacing.x7" : "0px",
            large: isSticky ? "base.spacing.x18" : "base.spacing.x12",
          },
          pr: {
            default: "unset",
            medium: isSticky ? "base.spacing.x5" : undefined,
            large: isSticky ? "base.spacing.x22" : undefined,
          },
          top: "-30px",
          mx: {
            default: "unset",
            medium: isSticky ? `-${base.spacing.x6}` : "0px",
          },
          w: {
            default: "100%",
            medium: isSticky ? `calc(100% + ${base.spacing.x12})` : "100%",
            large: isSticky ? `calc(100% + ${base.spacing.x16})` : "100%",
          },
          zIndex: isSticky ? 4 : "unset",
          backdropFilter: isSticky ? "base.frost.200" : "unset",
          transitionProperty: "padding",
          ...MOTION_SX,

          "&::before": {
            content: isSticky ? '""' : "unset",
            position: "absolute",
            top: "0px",
            left: "0px",
            w: "100%",
            h: "180%",
            zIndex: -1,
            bg: `linear-gradient(180deg, ${base.color.neutral[700]} 0%, transparent 100%)`,
          },
        }}
        className="TwitchDropsGameHeader"
      >
        <Stack
          direction="row"
          justifyContent={isLessThanMedium ? "center" : "flex-start"}
          alignItems="center"
          sx={{
            transitionProperty: "padding",
            ...MOTION_SX,
            pt: {
              default: isLessThanMedium ? "" : "base.spacing.x2",
              medium: isSticky ? "base.spacing.x2" : "base.spacing.x14",
            },
            pb: isSticky
              ? "base.spacing.x2"
              : isLessThanMedium
                ? "base.spacing.x2"
                : "base.spacing.x6",
          }}
        >
          {gameData.details.logo_url && (
            <CloudImage
              imageUrl={gameData.details.logo_url}
              relativeImageSizeInLayout="240px"
              sx={{
                flexShrink: 0,
                w: {
                  default: isLessThanMedium ? "100px" : "80px",
                  medium: "unset",
                },
                maxw: {
                  default: "180px",
                  medium: isSticky ? "180px" : "240px",
                },
                maxh: {
                  default: "60px",
                  medium: isSticky ? "52px" : "96px",
                },
                objectFit: "contain",
                zIndex: 1,
                transitionProperty: "width, height",
                ...MOTION_SX,
              }}
            />
          )}
          {stickyPageTitle && (
            <Heading
              size="small"
              sx={{
                ml: "base.spacing.x6",
                d: { default: "none", xLarge: "block" },
              }}
            >
              {stickyPageTitle}
            </Heading>
          )}
          <Stack
            direction="row"
            sx={{ d: { default: "none", medium: "flex" }, ml: "auto" }}
          >
            {playLink !== "#" && (
              <PlayButton
                buttonSize="small"
                playLink={playLink}
                analyticsEvent={onPlayButtonClick}
              />
            )}
          </Stack>
        </Stack>
        {!isLessThanMedium && <Divider />}
      </Box>
    </>
  );
};
